import React, { useEffect, useRef } from "react";
import { useFormikContext } from "formik";
import { NumberInput, SingleSelect, Button } from "@loanpal/lumos__core";
import { Box, Spinner, Text } from "theme-ui";
import ReCAPTCHA from "react-google-recaptcha";
import {
  numberToMoney,
  numberToMoneyNoDecimal,
  moneyStringToNumber,
} from "./format";
import { resetEmbeddedPageScroll } from "./embed-iframe-guard";

const DEBOUNCE_MS = 2000;
const DEFAULT_CAPTCHA_SITEKEY = "6Ld6Kb8hAAAAADN-z4PFwBU-mBk8HcGaWkKtmSs-";

/** State-specific limitation message, or category min/max fallback. */
export function getLoanAmountLimitError(
  amount,
  state,
  limitations,
  minAmount,
  maxAmount
) {
  const amt = moneyStringToNumber(amount);
  if (amount === "" || amount == null || Number.isNaN(amt)) return null;

  const stateLimits = limitations?.find((el) => el.state === state);
  if (stateLimits) {
    if (amt < stateLimits.minLoanAmount) {
      return `Loan amount needs to be greater than ${numberToMoneyNoDecimal(stateLimits.minLoanAmount)}`;
    }
    if (amt > stateLimits.maxLoanAmount) {
      return `Loan amount needs to be less than ${numberToMoneyNoDecimal(stateLimits.maxLoanAmount)}`;
    }
    return null;
  }

  if (minAmount != null && amt < minAmount) {
    return `Loan amount needs to be greater than ${numberToMoneyNoDecimal(minAmount)}`;
  }
  if (maxAmount != null && amt > maxAmount) {
    return `Loan amount needs to be less than ${numberToMoneyNoDecimal(maxAmount)}`;
  }
  return null;
}

/** Yup min check — skip until category min is loaded; allow amount >= min. */
export function isLoanAmountAboveMin(val, minAmount) {
  if (minAmount == null) return true;
  const amt = moneyStringToNumber(val);
  if (Number.isNaN(amt)) return false;
  return amt >= minAmount;
}

/** Yup max check — skip until category max is loaded; allow amount <= max. */
export function isLoanAmountBelowMax(val, maxAmount) {
  if (maxAmount == null) return true;
  const amt = moneyStringToNumber(val);
  if (Number.isNaN(amt)) return false;
  return amt <= maxAmount;
}

function validateLoanAmountDebounced({
  amt,
  values,
  limitations,
  minAmount,
  maxAmount,
  setCanApply,
  onValid,
}) {
  // Silent while typing — field error is shown on blur only.
  const limitError = getLoanAmountLimitError(
    amt,
    values.state,
    limitations,
    minAmount,
    maxAmount
  );
  if (limitError) {
    setCanApply(false);
    return;
  }
  onValid();
}

function clearLoanAmountFieldError(errors, setErrors) {
  if (!errors?.loanAmount) return;
  const next = { ...errors };
  delete next.loanAmount;
  setErrors(next);
}

function applyLoanAmountLimitCheck({
  amount,
  state,
  limitations,
  minAmount,
  maxAmount,
  errors,
  setFieldError,
  setErrors,
  setCanApply,
  showError = true,
}) {
  const limitError = getLoanAmountLimitError(
    amount,
    state,
    limitations,
    minAmount,
    maxAmount
  );
  if (limitError) {
    if (showError) setFieldError("loanAmount", limitError);
    setCanApply(false);
    return false;
  }
  clearLoanAmountFieldError(errors, setErrors);
  setCanApply(true);
  return true;
}

/** Re-check when limitations/state change (not on every amount keystroke). */
export function LoanAmountLimitsWatcher({
  limitations,
  minAmount,
  maxAmount,
  setCanApply,
}) {
  const { values, errors, setFieldError, setErrors } = useFormikContext();

  useEffect(() => {
    if (!values?.loanAmount || !values?.state) return;
    if (
      minAmount == null &&
      maxAmount == null &&
      !(Array.isArray(limitations) && limitations.length)
    ) {
      return;
    }
    applyLoanAmountLimitCheck({
      amount: values.loanAmount,
      state: values.state,
      limitations,
      minAmount,
      maxAmount,
      errors,
      setFieldError,
      setErrors,
      setCanApply,
      showError: true,
    });
    // Intentionally omit values.loanAmount — amount errors display on blur only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    limitations,
    minAmount,
    maxAmount,
    values.state,
    setFieldError,
    setErrors,
    setCanApply,
  ]);

  return null;
}

/** Stable state select — must be module-scoped so parent re-renders do not remount it. */
export function ApplyStatesInput({
  statesItems,
  limitations,
  minAmount,
  maxAmount,
  setLoadingPayment,
  setCanApply,
  readOnly = false,
}) {
  const { setFieldError, setErrors, values } = useFormikContext();
  return (
    <SingleSelect
      name="state"
      label="State"
      size={20}
      items={statesItems}
      readOnly={readOnly}
      dangerouslyOnChange={(target) => {
        if (values.loanAmount !== "") {
          setLoadingPayment(true);
          setErrors({});
          const limitError = getLoanAmountLimitError(
            values.loanAmount,
            target,
            limitations,
            minAmount,
            maxAmount
          );
          if (limitError) {
            setFieldError("loanAmount", limitError);
            setCanApply(false);
          } else {
            setLoadingPayment(false);
            setCanApply(true);
          }
        }
      }}
    />
  );
}

/** Reads loan amount from Formik so the payment card can update without a parent state bump. */
export function FormLoanAmountText(props) {
  const { values } = useFormikContext();
  const amount = moneyStringToNumber(values.loanAmount);
  return (
    <Text variant="h3" as="h2" {...props}>
      {numberToMoney(Number.isNaN(amount) ? 0 : amount)}
    </Text>
  );
}

/** Loan-amount calculator apply forms (apply, goodleapplus, sunrun). */
export function LoanAmountInput({
  isEmbedded = false,
  readOnly = false,
  singleOffer,
  limitations,
  minAmount,
  maxAmount,
  setPaymentAmount,
  setCanApply,
}) {
  const { setFieldValue, setFieldError, setErrors, values, errors } =
    useFormikContext();
  const debounceRef = useRef(null);
  const valuesRef = useRef(values);
  const limitsRef = useRef({ limitations, minAmount, maxAmount, singleOffer });

  valuesRef.current = values;
  limitsRef.current = { limitations, minAmount, maxAmount, singleOffer };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Re-validate displayed error when state/limitations change (not while typing amount).
  useEffect(() => {
    if (!values.loanAmount || !values.state) return;
    if (!singleOffer) return;
    applyLoanAmountLimitCheck({
      amount: values.loanAmount,
      state: values.state,
      limitations,
      minAmount,
      maxAmount,
      errors,
      setFieldError,
      setErrors,
      setCanApply,
      showError: true,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    values.state,
    limitations,
    minAmount,
    maxAmount,
    singleOffer,
    setFieldError,
    setErrors,
    setCanApply,
  ]);

  return (
    <NumberInput
      key="loanAmount"
      label="Amount to finance"
      name="loanAmount"
      autoComplete="off"
      readOnly={readOnly}
      onFocusCapture={(e) => {
        if (!isEmbedded) return;
        if (document.documentElement.getAttribute("data-embed-lock-scroll") !== "true") return;
        e.target.blur();
        resetEmbeddedPageScroll();
      }}
      onBlurCapture={({ target }) => {
        const moneyValue = moneyStringToNumber(target.value);
        if (!Number.isNaN(moneyValue)) {
          setFieldValue("loanAmount", numberToMoney(moneyValue));
        }
        applyLoanAmountLimitCheck({
          amount: Number.isNaN(moneyValue) ? target.value : numberToMoney(moneyValue),
          state: valuesRef.current.state,
          limitations: limitsRef.current.limitations,
          minAmount: limitsRef.current.minAmount,
          maxAmount: limitsRef.current.maxAmount,
          errors,
          setFieldError,
          setErrors,
          setCanApply,
          showError: true,
        });
      }}
      onChangeCapture={({ target }) => {
        const amt = target.value;
        setFieldValue("loanAmount", amt, false);
        // Hide error while typing; show again on blur if still invalid.
        clearLoanAmountFieldError(errors, setErrors);
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          const { limitations: lims, minAmount: min, maxAmount: max, singleOffer: offer } =
            limitsRef.current;
          if (!offer) return;
          validateLoanAmountDebounced({
            amt,
            values: valuesRef.current,
            limitations: lims,
            minAmount: min,
            maxAmount: max,
            setCanApply,
            onValid: () => {
              setPaymentAmount({
                amount: moneyStringToNumber(amt),
                payment: true,
              });
              setCanApply(true);
            },
          });
        }, DEBOUNCE_MS);
      }}
    />
  );
}

function validatePaymentAmountDebounced({
  amt,
  values,
  limitations,
  minAmount,
  maxAmount,
  singleOffer,
  getPaymentFactor,
  resolveLoanAmount,
  setFieldError,
  setErrors,
  setCanApply,
  onValid,
}) {
  setErrors({});
  const pf = getPaymentFactor(singleOffer);
  const computedLoan = moneyStringToNumber(resolveLoanAmount(amt, pf));

  if (limitations?.find((el) => el.state === values.state)) {
    const limits = limitations.find((el) => el.state === values.state);
    if (computedLoan < limits.minLoanAmount) {
      setFieldError(
        "paymentAmount",
        `Calculated loan amount needs to be greater than ${numberToMoneyNoDecimal(limits.minLoanAmount)}`
      );
      setCanApply(false);
      return;
    }
    if (computedLoan > limits.maxLoanAmount) {
      setFieldError(
        "paymentAmount",
        `Calculated loan amount needs to be less than ${numberToMoneyNoDecimal(limits.maxLoanAmount)}`
      );
      setCanApply(false);
      return;
    }
  } else {
    if (computedLoan < minAmount) {
      setFieldError(
        "paymentAmount",
        `Calculated loan amount needs to be greater than ${numberToMoneyNoDecimal(minAmount)}`
      );
      setCanApply(false);
      return;
    }
    if (computedLoan > maxAmount) {
      setFieldError(
        "paymentAmount",
        `Calculated loan amount needs to be less than ${numberToMoneyNoDecimal(maxAmount)}`
      );
      setCanApply(false);
      return;
    }
  }
  onValid(computedLoan);
}

/** Payment-amount calculator apply form (daikin). */
export function PaymentAmountInput({
  singleOffer,
  limitations,
  minAmount,
  maxAmount,
  calculatorType,
  setPaymentAmountObj,
  setCanApply,
  getPaymentFactor,
  resolveLoanAmount,
  label = "What is your budget for a monthly payment?",
}) {
  const { setFieldValue, setFieldError, setErrors, values } = useFormikContext();
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  return (
    <NumberInput
      key="paymentAmount"
      label={label}
      name="paymentAmount"
      autoComplete="off"
      onBlurCapture={({ target }) => {
        const moneyValue = moneyStringToNumber(target.value);
        if (Number.isNaN(moneyValue)) return;
        setFieldValue("paymentAmount", numberToMoney(moneyValue));
      }}
      onChangeCapture={({ target }) => {
        const moneyValue = moneyStringToNumber(target.value);
        const amt = target.value;
        setPaymentAmountObj({
          paymentAmount: moneyStringToNumber(target.value),
          loanAmount: 0,
          payment: false,
          monthly: calculatorType,
        });
        setFieldValue("paymentAmount", numberToMoney(moneyValue));
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => {
          if (!singleOffer) return;
          validatePaymentAmountDebounced({
            amt,
            values,
            limitations,
            minAmount,
            maxAmount,
            singleOffer,
            getPaymentFactor,
            resolveLoanAmount,
            setFieldError,
            setErrors,
            setCanApply,
            onValid: (computedLoan) => {
              setPaymentAmountObj({
                paymentAmount: moneyStringToNumber(amt),
                loanAmount: computedLoan,
                payment: true,
                monthly: calculatorType,
              });
              setCanApply(true);
            },
          });
        }, DEBOUNCE_MS);
      }}
    />
  );
}

/**
 * Captcha + submit gated by loan amount state limits.
 * Must render inside Formik/Form so amount errors keep Apply disabled after captcha.
 */
export function ApplyCaptchaAndSubmit({
  spinner = false,
  canApply,
  setCanApply,
  setCaptchaResponse,
  captchaRef,
  limitations,
  minAmount,
  maxAmount,
  checkingSubmittingUser = false,
  submitLabel = "APPLY NOW",
  spinnerColor = "#FF8300",
  sitekey = DEFAULT_CAPTCHA_SITEKEY,
  loanAmountBlockedRef,
  buttonSx,
  spinnerSx,
  /** Optional: return a string error to block submit (e.g. paymentAmount forms). */
  resolveLimitError,
  /** Optional: custom submit actions. Receives { enabled, spinner }. */
  renderActions,
  onCaptchaExpired,
}) {
  const { errors, values, setFieldError } = useFormikContext();

  const defaultLimitError =
    errors.loanAmount ||
    getLoanAmountLimitError(
      values.loanAmount,
      values.state,
      limitations,
      minAmount,
      maxAmount
    );

  const limitError =
    typeof resolveLimitError === "function"
      ? resolveLimitError({ errors, values }) || defaultLimitError
      : defaultLimitError;

  const enabled =
    !spinner && canApply && !limitError && !checkingSubmittingUser;

  const handleCaptchaChange = (value) => {
    setCaptchaResponse(value || "");
    const amountError =
      typeof resolveLimitError === "function"
        ? resolveLimitError({ errors, values }) ||
          getLoanAmountLimitError(
            values.loanAmount,
            values.state,
            limitations,
            minAmount,
            maxAmount
          )
        : getLoanAmountLimitError(
            values.loanAmount,
            values.state,
            limitations,
            minAmount,
            maxAmount
          );
    if (amountError) {
      if (!errors.paymentAmount) {
        setFieldError("loanAmount", amountError);
      }
      if (loanAmountBlockedRef) loanAmountBlockedRef.current = true;
      setCanApply(false);
      return;
    }
    if (loanAmountBlockedRef) loanAmountBlockedRef.current = false;
    setCanApply(!!value);
  };

  const handleCaptchaExpired = () => {
    setCaptchaResponse("");
    setCanApply(false);
    captchaRef.current?.reset?.();
    onCaptchaExpired?.();
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-start",
          pt: 3,
          my: 3,
          borderTop: "1px solid",
          borderTopColor: "neutral.l3",
          pointerEvents: checkingSubmittingUser ? "none" : "auto",
          opacity: checkingSubmittingUser ? 0.65 : 1,
        }}
      >
        <ReCAPTCHA
          ref={captchaRef}
          sitekey={sitekey}
          onChange={handleCaptchaChange}
          onExpired={handleCaptchaExpired}
          onErrored={handleCaptchaExpired}
        />
      </Box>
      {typeof renderActions === "function" ? (
        renderActions({ enabled, spinner, limitError })
      ) : (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            my: 3,
          }}
        >
          {spinner && (
            <>
              <Button type="submit" sx={{ mt: 3, px: 5, ...buttonSx }} disabled>
                {submitLabel}
              </Button>
              <Spinner
                title="loading"
                size="32"
                sx={{ mt: 3, px: 3, color: spinnerColor, ...spinnerSx }}
              />
            </>
          )}
          {!spinner && (
            <Button
              type="submit"
              sx={{ mt: 3, px: 5, ...buttonSx }}
              disabled={!enabled}
            >
              {submitLabel}
            </Button>
          )}
        </Box>
      )}
    </>
  );
}

/** Keep amount-limit blocks from being overridden by captcha enabling Apply. */
export function createAmountGatedSetCanApply({
  setCanApply,
  captchaRef,
  getCaptchaResponse,
  loanAmountBlockedRef,
}) {
  return (amountOk) => {
    if (loanAmountBlockedRef) {
      loanAmountBlockedRef.current = !amountOk;
    }
    if (!amountOk) {
      setCanApply(false);
      return;
    }
    const captchaOk = !!(
      captchaRef?.current?.getValue?.() || getCaptchaResponse?.()
    );
    setCanApply(captchaOk);
  };
}
